// Mobile full-screen player profile — the "one tap deeper" view behind the compact
// roster card. Reuses the desktop `player` worker view (src/worker/views/player.ts)
// verbatim: ratings (with per-season deltas), per-game + advanced stats across
// Regular / Playoffs / Combined, shot locations, game highs, salaries, bio, awards.
//
// The compact card (name, OVR/POT/Salary/Exp, Trade/Cut) "grows up" into the hero at
// the top of this screen; tapping a card on GmRoster / GmStats routes here.
//
// Design handoff: design_handoff_player_card. Colors/tiers/positions per that spec.

import { useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import { useNoTradingAllowed } from "../util/noTradingAllowed.ts";
import { untradableMessage } from "../util/untradable.ts";
import { toWorker } from "../util/toWorker.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { logEvent } from "../util/logEvent.ts";
import { helpers } from "../util/helpers.ts";
import { confirmCutPlayer } from "../util/confirmCutPlayer.ts";
import { groupAwards } from "../util/groupAwards.ts";
import { PHASE, PLAYER, SKILLS, orderSkills } from "../../common/constants.ts";
import type { View } from "../../common/types.ts";

// ---- color / tier / position systems (design handoff) ----
const ratingColor = (v: number) =>
	v >= 80 ? "#18E0A8" : v >= 62 ? "#4fd6a8" : v >= 48 ? "#E8B923" : "#8f9aa2";

const POS_COLORS: Record<string, string> = {
	PG: "#4A9EFF",
	SG: "#2a7de1",
	SF: "#18E0A8",
	PF: "#E8B923",
	C: "#E2504E",
	GF: "#7C9FF0",
	FC: "#E8A020",
	F: "#9a6cff",
	G: "#4A9EFF",
};
const posColor = (p: string) => POS_COLORS[p] ?? "#4A9EFF";

const money = (m: number) =>
	`$${m >= 100 ? Math.round(m) : Math.round(m * 10) / 10}M`;

// change delta: +n teal, -n red, — gray
const delta = (d: number | undefined) => {
	if (!d || Number.isNaN(d)) return { t: "—", c: "#5f6b73" };
	return d > 0 ? { t: `+${d}`, c: "#18E0A8" } : { t: String(d), c: "#E2504E" };
};

const initialsOf = (name: string) =>
	name
		.split(" ")
		.map((w) => w[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

// Rating groups: label + rating key (matches desktop RatingsOverview).
const RATING_GROUPS: { name: string; items: [string, string][] }[] = [
	{
		name: "PHYSICAL",
		items: [
			["Height", "hgt"],
			["Strength", "stre"],
			["Speed", "spd"],
			["Jumping", "jmp"],
			["Endurance", "endu"],
		],
	},
	{
		name: "SHOOTING",
		items: [
			["Inside", "ins"],
			["Layups", "dnk"],
			["FT", "ft"],
			["Mid Range", "fg"],
			["3 Pointers", "tp"],
		],
	},
	{
		name: "SKILL",
		items: [
			["Off IQ", "oiq"],
			["Def IQ", "diq"],
			["Dribbling", "drb"],
			["Passing", "pss"],
			["Rebounding", "reb"],
		],
	},
];

// Per-game columns → stat keys
const PG_COLS: [string, string][] = [
	["GP", "gp"],
	["GS", "gs"],
	["MP", "min"],
	["PTS", "pts"],
	["TRB", "trb"],
	["AST", "ast"],
	["FG%", "fgp"],
	["3P%", "tpp"],
	["FT%", "ftp"],
	["STL", "stl"],
	["BLK", "blk"],
	["TOV", "tov"],
];
// Advanced columns → stat keys (usgp/ortg/drtg/ewa/ws are real advanced stats)
const ADV_COLS: [string, string][] = [
	["GP", "gp"],
	["GS", "gs"],
	["MP", "min"],
	["PER", "per"],
	["TS%", "tsp"],
	["USG%", "usgp"],
	["ORTG", "ortg"],
	["DRTG", "drtg"],
	["EWA", "ewa"],
	["WS", "ws"],
];
// Shot-location zones: label, made key, attempts key
const SHOT_ZONES: [string, string, string][] = [
	["At Rim", "fgAtRim", "fgaAtRim"],
	["Low Post", "fgLowPost", "fgaLowPost"],
	["Mid Range", "fgMidRange", "fgaMidRange"],
	["3-pointers", "tp", "tpa"],
];
// Game highs: label, Max stat key
const HIGH_COLS: [string, string][] = [
	["PTS", "ptsMax"],
	["TRB", "trbMax"],
	["AST", "astMax"],
	["STL", "stlMax"],
	["BLK", "blkMax"],
	["MP", "minMax"],
	["FG", "fgMax"],
	["3P", "tpMax"],
];

type StatType = "reg" | "po" | "cb";

const GmPlayer = ({ player: p, teamName }: View<"player">) => {
	// The view manager swaps in a dedicated error component when the worker view
	// returns `errorMessage`, so this component always receives a valid player.

	useTitleBar({ title: p.name, hideNewWindow: true });
	const {
		lid,
		phase: localPhase,
		season: currentSeason,
		userTid,
	} = useLocal(["lid", "phase", "season", "userTid"]);
	const noTradingAllowed = useNoTradingAllowed();

	const [stab, setStab] = useState<StatType>("reg");
	const [open, setOpen] = useState({
		pg: true,
		adv: false,
		shot: false,
		hi: false,
		sal: true,
	});
	const [note, setNote] = useState<string>(p.note ?? "");
	const [watch, setWatch] = useState<number>(p.watch ?? 0);
	const [toast, setToast] = useState<string>("");

	const flash = (m: string) => {
		setToast(m);
		window.setTimeout(() => setToast(""), 1900);
	};

	const toggle = (k: keyof typeof open) =>
		setOpen((o) => ({ ...o, [k]: !o[k] }));

	const goBack = () => realtimeUpdate([], `/l/${lid}/roster`);

	const onCut = async () => {
		const ok = await confirmCutPlayer(p, p.name, localPhase, currentSeason);
		if (!ok) return;
		const err: any = await toWorker("main", "releasePlayer", { pids: [p.pid] });
		if (err) {
			logEvent({ type: "error", text: err, saveToDb: false });
		} else {
			logEvent({
				type: "success",
				text: `${p.name} released.`,
				saveToDb: false,
			});
			realtimeUpdate([], `/l/${lid}/roster`);
		}
	};

	const onTrade = async () => {
		// A recently-signed player can't be added to a trade — the worker silently drops them, so
		// surface the reason instead of opening an empty deal.
		const untradable = untradableMessage(p);
		if (untradable) {
			flash(untradable);
			return;
		}
		// Open the mobile Trade wizard seeded with this player — NOT actions.addToTradingBlock,
		// which navigates to the desktop trading-block UI. If it's our player we put him on our
		// side; otherwise we start acquiring him from his team.
		await toWorker("main", "createTrade", [
			{
				tid: userTid,
				pids: mine ? [p.pid] : [],
				dpids: [],
				pidsExcluded: [],
				dpidsExcluded: [],
			},
			mine
				? { tid: -1, pids: [], dpids: [], pidsExcluded: [], dpidsExcluded: [] }
				: {
						tid: p.tid,
						pids: [p.pid],
						dpids: [],
						pidsExcluded: [],
						dpidsExcluded: [],
					},
		] as any);
		realtimeUpdate([], `/l/${lid}/trade`);
	};

	const saveNote = (v: string) => {
		setNote(v);
		toWorker("main", "setNote", {
			type: "player",
			pid: p.pid,
			editedNote: v,
		}).catch(() => {});
	};

	const toggleWatch = () => {
		const next = watch >= 1 ? 0 : 1;
		setWatch(next);
		toWorker("main", "updatePlayerWatch", { pid: p.pid, watch: next }).catch(
			() => {},
		);
		flash(next ? `Watching ${p.name}.` : `Removed ${p.name} from watch list.`);
	};

	// ---- derived: current & previous rating rows (for deltas) ----
	const ratingRows = p.ratings;
	const curR: any = ratingRows.at(-1) ?? {};
	const prevR: any =
		ratingRows.findLast((r: any) => r.season === curR.season - 1) ?? curR;

	const ovr = curR.ovr ?? 0;
	const pot = curR.pot ?? 0;
	const pos = curR.pos ?? "";
	const skills: string[] = curR.skills ?? [];

	const isUser = p.tid >= 0;
	// Whether this player is on the user's own team. Roster lets you browse rival teams and open
	// their players' cards — there you may only trade FOR them, never cut or trade them away.
	const mine = p.tid === userTid;
	const exp = p.contract?.exp;
	const salary = p.contract?.amount ?? 0;

	// ---- stat rows split by season type ----
	// playersPlus stat rows carry `playoffs`: false (regular), true (playoffs),
	// or the string "combined" (games-weighted merge of the two).
	const allStats: any[] = p.stats ?? [];
	const regRows = allStats.filter((s) => s.playoffs === false);
	const poRows = allStats.filter((s) => s.playoffs === true);
	const cbRows = allStats.filter((s) => s.playoffs === "combined");
	const baseRows = stab === "po" ? poRows : stab === "cb" ? cbRows : regRows;
	const careerRow =
		stab === "po"
			? p.careerStatsPlayoffs
			: stab === "cb"
				? p.careerStatsCombined
				: p.careerStats;
	const careerHasStats = (careerRow?.gp ?? 0) > 0;

	// Latest regular-season row + career for the Snapshot
	const curStat: any = regRows.at(-1) ?? {};

	// ---- context chip ----
	const phaseNum = localPhase;
	const phaseLabel =
		phaseNum === PHASE.PLAYOFFS
			? "Playoffs"
			: phaseNum === PHASE.DRAFT ||
				  phaseNum === PHASE.AFTER_DRAFT ||
				  phaseNum === PHASE.DRAFT_LOTTERY
				? "Draft"
				: phaseNum === PHASE.FREE_AGENCY || phaseNum === PHASE.RESIGN_PLAYERS
					? "Free Agency"
					: phaseNum === PHASE.PRESEASON
						? "Preseason"
						: "Regular Season";
	const contextText = `${phaseLabel} · ${currentSeason}`;

	// ---- bio ----
	const bornPlace = [p.born?.loc].filter(Boolean).join("");
	const bio: { k: string; v: string; color: string }[] = [
		{ k: "Position", v: pos || "—", color: "#fff" },
		{
			k: "Team",
			v: `${teamName}${p.jerseyNumber ? ` · #${p.jerseyNumber}` : ""}`,
			color: "#4A9EFF",
		},
		{
			k: "Height / Weight",
			v: `${Math.floor(p.hgt / 12)}'${p.hgt % 12}" · ${p.weight} lbs`,
			color: "#fff",
		},
		{
			k: "Born",
			v: `${bornPlace ? `${bornPlace} · ` : ""}${p.born?.year ?? "—"}`,
			color: "#fff",
		},
		{
			k: "Draft",
			v:
				p.draft?.round > 0
					? `${p.draft.year} · Rd ${p.draft.round}, Pick ${p.draft.pick}`
					: `${p.draft?.year ?? "—"} · Undrafted`,
			color: "#fff",
		},
		{ k: "College", v: p.college || "—", color: "#4A9EFF" },
		{ k: "Experience", v: `${p.experience} years`, color: "#fff" },
		{
			k: "Contract",
			v: isUser ? `${money(salary)}/yr thru ${exp}` : "—",
			color: "#18E0A8",
		},
	];

	// ---- awards ----
	const awardsGrouped = groupAwards(p.awards ?? []);
	const awardIcon = (t: string) =>
		t.includes("All-League") || t.includes("Champion")
			? "🏆"
			: t.includes("All-Star")
				? "⭐"
				: t.includes("Rookie")
					? "🎖"
					: "🏅";

	// ---- snapshot ----
	const snapStats = (r: any) =>
		[
			["PTS", "pts"],
			["TRB", "trb"],
			["AST", "ast"],
			["FG%", "fgp"],
			["3P%", "tpp"],
			["FT%", "ftp"],
		].map(([k, key]) => ({ k, v: helpers.roundStat(r[key] ?? 0, key) }));
	const snapshot = [
		{
			label: `${currentSeason} season`,
			color: "#18E0A8",
			stats: snapStats(curStat),
		},
		{
			label: `Career · ${p.careerStats?.gp ?? 0} GP`,
			color: "#8fb3c9",
			stats: snapStats(p.careerStats ?? {}),
		},
	];

	// ---- shot locations (latest regular-season row) ----
	const shotSource: any = curStat;
	const shotZones = SHOT_ZONES.map(([zone, mk, ak]) => {
		const m = shotSource[mk] ?? 0;
		const a = shotSource[ak] ?? 0;
		const pct = a ? Math.round((m / a) * 1000) / 10 : 0;
		return { zone, m, a, pct };
	});
	const shotMax = Math.max(1, ...shotZones.map((z) => z.a));
	const feats = [
		["Dbl-Dbls", "dd"],
		["Trp-Dbls", "td"],
		["Quad-Dbls", "qd"],
	].map(([label, key]) => ({ label, v: Math.round(shotSource[key] ?? 0) }));

	// ---- salaries (amounts already in millions from playersPlus) ----
	const salaries = (p.salaries ?? []).map((s: any) => ({
		year: s.season,
		amt: s.amount,
		future: s.type === "future",
	}));
	const salMax = Math.max(1, ...salaries.map((s) => s.amt));
	const salTotal = p.salariesTotal ?? salaries.reduce((n, s) => n + s.amt, 0);

	// ---- shared bits ----
	const ovrCol = ratingColor(ovr);
	const potCol = ratingColor(pot);
	const ovrD = delta(ovr - (prevR.ovr ?? ovr));
	const potD = delta(pot - (prevR.pot ?? pot));

	const StatTile = ({ v, l, color }: { v: any; l: string; color?: string }) => (
		<div
			style={{
				background: "#111",
				border: "1px solid #262626",
				borderRadius: 11,
				padding: "10px 4px",
				textAlign: "center",
			}}
		>
			<div
				style={{
					fontSize: 19,
					fontWeight: 900,
					letterSpacing: "-.3px",
					color: color ?? "#fff",
				}}
			>
				{v}
			</div>
			<div
				style={{
					fontSize: 8.5,
					fontWeight: 800,
					letterSpacing: ".6px",
					color: "#7f8b93",
					marginTop: 2,
				}}
			>
				{l}
			</div>
		</div>
	);

	const SectionHead = ({
		title,
		k,
		right,
	}: {
		title: string;
		k: keyof typeof open;
		right?: any;
	}) => (
		<div
			onClick={() => toggle(k)}
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				marginTop: 16,
				padding: "2px 0",
				cursor: "pointer",
			}}
		>
			<div style={{ fontSize: 15, fontWeight: 900, letterSpacing: "-.2px" }}>
				{title}
				{right}
			</div>
			<div style={{ fontSize: 14, fontWeight: 900, color: "#7f8b93" }}>
				{open[k] ? "▾" : "▸"}
			</div>
		</div>
	);

	// dense sticky-column stat table (per-game / advanced)
	const StatTable = ({
		cols,
		rows,
		career,
		careerShown,
		colW,
		advanced,
	}: {
		cols: [string, string][];
		rows: any[];
		career: any;
		careerShown: boolean;
		colW: number;
		advanced?: boolean;
	}) => {
		if (rows.length === 0) {
			return (
				<div
					style={{
						padding: 20,
						textAlign: "center",
						fontSize: 12,
						fontWeight: 700,
						color: "#7a7a7a",
					}}
				>
					{stab === "po" ? "No playoff appearances." : "No stats yet."}
				</div>
			);
		}
		const cell = (r: any, key: string, isCareer: boolean) => {
			const raw = r[key];
			const v =
				raw === undefined || raw === null ? "" : helpers.roundStat(raw, key);
			// highlight strong advanced values
			let color = key === "pts" && !advanced ? "#fff" : "#d6d6d6";
			if (advanced && key === "per" && (raw ?? 0) >= 20) color = "#18E0A8";
			if (advanced && key === "tsp" && (raw ?? 0) >= 58) color = "#18E0A8";
			return (
				<span
					key={key}
					style={{
						width: colW,
						flex: "none",
						textAlign: "right",
						fontSize: 12,
						fontWeight: isCareer ? 900 : 800,
						color,
					}}
				>
					{v}
				</span>
			);
		};
		const Row = ({ r, isCareer }: { r: any; isCareer: boolean }) => (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					padding: "8px 0",
					borderTop: `${isCareer ? 1.5 : 1}px solid ${isCareer ? "#2f2f2f" : "#202020"}`,
				}}
			>
				<div
					style={{
						position: "sticky",
						left: 0,
						zIndex: 2,
						width: 88,
						flex: "none",
						background: "#121212",
						display: "flex",
						alignItems: "center",
						gap: 5,
						paddingRight: 8,
					}}
				>
					<div
						style={{
							fontSize: 12,
							fontWeight: 900,
							color: isCareer ? "#fff" : "#4A9EFF",
						}}
					>
						{isCareer ? "Career" : r.season}
					</div>
					{!isCareer ? (
						<div style={{ fontSize: 9, fontWeight: 800, color: "#5f6b73" }}>
							{r.abbrev}
						</div>
					) : null}
				</div>
				<div style={{ display: "flex", flex: "none" }}>
					{cols.map(([, key]) => cell(r, key, isCareer))}
				</div>
			</div>
		);
		return (
			<div className="gmp-scroll" style={{ overflowX: "auto", marginTop: 8 }}>
				<div style={{ minWidth: 88 + cols.length * colW }}>
					<div
						style={{
							display: "flex",
							alignItems: "flex-end",
							paddingBottom: 7,
							borderBottom: "1px solid #2a2a2a",
						}}
					>
						<div
							style={{
								position: "sticky",
								left: 0,
								zIndex: 2,
								width: 88,
								flex: "none",
								background: "#121212",
								fontSize: 8.5,
								fontWeight: 800,
								color: "#6f7b83",
								letterSpacing: ".4px",
							}}
						>
							SEASON
						</div>
						<div style={{ display: "flex", flex: "none" }}>
							{cols.map(([label]) => (
								<div
									key={label}
									style={{
										width: colW,
										flex: "none",
										textAlign: "right",
										fontSize: 8.5,
										fontWeight: 800,
										color: "#7f8b93",
									}}
								>
									{label}
								</div>
							))}
						</div>
					</div>
					{rows.map((r) => (
						<Row key={`${r.season}-${r.tid}`} r={r} isCareer={false} />
					))}
					{careerShown ? <Row r={career} isCareer={true} /> : null}
				</div>
			</div>
		);
	};

	const seg = (active: boolean) => ({
		flex: 1,
		textAlign: "center" as const,
		padding: "8px 4px",
		borderRadius: 9,
		fontSize: 11,
		fontWeight: 900,
		cursor: "pointer",
		...(active
			? { background: "#18E0A8", color: "#053a2c" }
			: { color: "#8a8a8a" }),
	});

	return (
		<div
			style={{ color: "#fff", fontFamily: "Montserrat,system-ui,sans-serif" }}
		>
			<style>{styles}</style>

			{/* top bar */}
			<div className="gmp-topbar">
				<div onClick={goBack} className="gmp-round">
					‹
				</div>
				<div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
					<div
						style={{
							fontSize: 15,
							fontWeight: 900,
							letterSpacing: "-.2px",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{p.name}
					</div>
					<div style={{ fontSize: 10, fontWeight: 700, color: "#8fb3c9" }}>
						{pos} · {teamName}
						{p.jerseyNumber ? ` · #${p.jerseyNumber}` : ""}
					</div>
				</div>
				<div className="gmp-round" style={{ fontSize: 15 }}>
					⚙
				</div>
			</div>

			<div className="gmp-body">
				{/* context chip */}
				<div className="gmp-chip">
					<span className="gmp-dot" />
					{contextText}
				</div>

				{/* hero card */}
				<div className="gmp-hero">
					<div style={{ display: "flex", alignItems: "center", gap: 13 }}>
						<div className="gmp-av" style={{ background: posColor(pos) }}>
							{initialsOf(p.name)}
							{p.jerseyNumber ? (
								<div className="gmp-av-badge">{p.jerseyNumber}</div>
							) : null}
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div
								style={{
									fontSize: 23,
									fontWeight: 900,
									letterSpacing: "-.6px",
									lineHeight: 1.05,
								}}
							>
								{p.name}
							</div>
							<div
								style={{
									fontSize: 11.5,
									fontWeight: 700,
									color: "#8fb3c9",
									marginTop: 3,
								}}
							>
								{pos} · Age {p.age} · {Math.floor(p.hgt / 12)}'{p.hgt % 12}" ·{" "}
								{p.weight} lbs
							</div>
							<div
								style={{
									display: "flex",
									gap: 5,
									marginTop: 8,
									flexWrap: "wrap",
								}}
							>
								{orderSkills(skills).map((k) => (
									<div key={k} className="gmp-skill" title={SKILLS[k] ?? k}>
										{k}
									</div>
								))}
								{watch ? <div className="gmp-tag">★ Watching</div> : null}
							</div>
						</div>
					</div>

					<div className="gmp-tiles">
						<StatTile v={ovr} l="OVR" color={ovrCol} />
						<StatTile v={pot} l="POT" color={potCol} />
						<StatTile v={isUser ? money(salary) : "—"} l="SALARY" />
						<StatTile v={exp ? `'${String(exp).slice(-2)}` : "—"} l="EXP" />
					</div>

					<div style={{ display: "flex", gap: 9, marginTop: 12 }}>
						{!noTradingAllowed ? (
							<div onClick={onTrade} className="gmp-btn-trade">
								{mine ? "Trade" : "Trade for"}
							</div>
						) : null}
						{mine ? (
							<div onClick={onCut} className="gmp-btn-cut">
								Cut
							</div>
						) : null}
					</div>
				</div>

				{/* secondary actions */}
				<div className="gmp-actions">
					{[
						...(mine && !noTradingAllowed
							? [{ icon: "📋", label: "Trading block", onTap: onTrade }]
							: []),
						{
							icon: "⇄",
							label: "Compare",
							onTap: () =>
								realtimeUpdate(
									[],
									helpers.leagueUrl(["compare_players", `${p.pid}-career-r`]),
								),
						},
						{
							icon: "✎",
							label: "Edit",
							onTap: () =>
								realtimeUpdate(
									[],
									helpers.leagueUrl(["customize_player", p.pid]),
								),
						},
						{
							icon: watch ? "★" : "☆",
							label: watch ? "Unwatch" : "Watch",
							onTap: toggleWatch,
						},
					].map((a) => (
						<div key={a.label} onClick={a.onTap} className="gmp-action">
							<div style={{ fontSize: 16 }}>{a.icon}</div>
							<div
								style={{
									fontSize: 9.5,
									fontWeight: 800,
									color: "#9fb0bc",
									marginTop: 5,
								}}
							>
								{a.label}
							</div>
						</div>
					))}
				</div>

				{/* overall / potential deltas */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						gap: 10,
						marginTop: 14,
					}}
				>
					<div className="gmp-op">
						<div className="gmp-op-l">OVERALL</div>
						<div
							style={{
								display: "flex",
								alignItems: "baseline",
								gap: 7,
								marginTop: 3,
							}}
						>
							<div
								style={{
									fontSize: 32,
									fontWeight: 900,
									letterSpacing: "-1px",
									color: ovrCol,
								}}
							>
								{ovr}
							</div>
							<div style={{ fontSize: 13, fontWeight: 900, color: ovrD.c }}>
								{ovrD.t}
							</div>
						</div>
					</div>
					<div className="gmp-op">
						<div className="gmp-op-l">POTENTIAL</div>
						<div
							style={{
								display: "flex",
								alignItems: "baseline",
								gap: 7,
								marginTop: 3,
							}}
						>
							<div
								style={{
									fontSize: 32,
									fontWeight: 900,
									letterSpacing: "-1px",
									color: potCol,
								}}
							>
								{pot}
							</div>
							<div style={{ fontSize: 13, fontWeight: 900, color: potD.c }}>
								{potD.t}
							</div>
						</div>
					</div>
				</div>

				{/* ratings */}
				<div className="gmp-h">Ratings</div>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 14,
						marginTop: 10,
					}}
				>
					{RATING_GROUPS.map((grp) => (
						<div key={grp.name} className="gmp-card">
							<div className="gmp-grp-name">{grp.name}</div>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: 9,
									marginTop: 10,
								}}
							>
								{grp.items.map(([label, key]) => {
									const v = curR[key] ?? 0;
									const d = delta(v - (prevR[key] ?? v));
									return (
										<div
											key={key}
											style={{ display: "flex", alignItems: "center", gap: 10 }}
										>
											<div className="gmp-rt-label">{label}</div>
											<div className="gmp-rt-track">
												<div
													style={{
														height: "100%",
														width: `${v}%`,
														borderRadius: 4,
														background: ratingColor(v),
													}}
												/>
											</div>
											<div
												style={{
													width: 26,
													flex: "none",
													textAlign: "right",
													fontSize: 13,
													fontWeight: 900,
													color: ratingColor(v),
												}}
											>
												{v}
											</div>
											<div
												style={{
													width: 24,
													flex: "none",
													textAlign: "right",
													fontSize: 10.5,
													fontWeight: 900,
													color: d.c,
												}}
											>
												{d.t}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					))}
				</div>

				{/* snapshot */}
				<div className="gmp-h">Snapshot</div>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 10,
						marginTop: 10,
					}}
				>
					{snapshot.map((row) => (
						<div
							key={row.label}
							className="gmp-card"
							style={{ padding: "11px 12px 12px" }}
						>
							<div
								style={{
									fontSize: 11,
									fontWeight: 900,
									letterSpacing: ".3px",
									color: row.color,
								}}
							>
								{row.label}
							</div>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "repeat(6,1fr)",
									gap: 4,
									marginTop: 9,
								}}
							>
								{row.stats.map((s) => (
									<div key={s.k} style={{ textAlign: "center" }}>
										<div
											style={{
												fontSize: 15,
												fontWeight: 900,
												letterSpacing: "-.3px",
											}}
										>
											{s.v}
										</div>
										<div
											style={{
												fontSize: 8,
												fontWeight: 800,
												letterSpacing: ".3px",
												color: "#7f8b93",
												marginTop: 2,
											}}
										>
											{s.k}
										</div>
									</div>
								))}
							</div>
						</div>
					))}
				</div>

				{/* bio */}
				<div className="gmp-h">Bio</div>
				<div
					className="gmp-card"
					style={{ padding: "4px 14px", marginTop: 10 }}
				>
					{bio.map((b, i) => (
						<div
							key={b.k}
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: 14,
								padding: "10px 0",
								borderTop: i === 0 ? "none" : "1px solid #232323",
							}}
						>
							<div
								style={{
									fontSize: 11.5,
									fontWeight: 700,
									color: "#8a8a8a",
									flex: "none",
								}}
							>
								{b.k}
							</div>
							<div
								style={{
									fontSize: 12.5,
									fontWeight: 800,
									textAlign: "right",
									color: b.color,
								}}
							>
								{b.v}
							</div>
						</div>
					))}
				</div>

				{/* awards */}
				<div className="gmp-h">Awards</div>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 8,
						marginTop: 10,
					}}
				>
					{awardsGrouped.length === 0 ? (
						<div
							style={{
								background: "#181818",
								border: "1px solid #232323",
								borderRadius: 11,
								padding: 13,
								fontSize: 12,
								fontWeight: 700,
								color: "#7a7a7a",
								textAlign: "center",
							}}
						>
							No awards yet.
						</div>
					) : (
						awardsGrouped.map((a) => (
							<div
								key={a.type}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 10,
									background: "#181818",
									border: "1px solid #232323",
									borderRadius: 11,
									padding: "11px 13px",
								}}
							>
								<div style={{ fontSize: 15, flex: "none" }}>
									{awardIcon(a.type)}
								</div>
								<div style={{ fontSize: 12.5, fontWeight: 800 }}>
									{a.count > 1 ? `${a.count}× ` : ""}
									{a.type}
									{a.seasons?.length ? (
										<span style={{ color: "#7f8b93", fontWeight: 700 }}>
											{" "}
											({a.seasons.join(", ")})
										</span>
									) : null}
								</div>
							</div>
						))
					)}
				</div>

				{/* season-type tabs */}
				<div
					style={{
						display: "flex",
						gap: 6,
						marginTop: 20,
						background: "#171717",
						border: "1px solid #242424",
						borderRadius: 12,
						padding: 4,
					}}
				>
					<div onClick={() => setStab("reg")} style={seg(stab === "reg")}>
						Regular Season
					</div>
					<div onClick={() => setStab("po")} style={seg(stab === "po")}>
						Playoffs
					</div>
					<div onClick={() => setStab("cb")} style={seg(stab === "cb")}>
						Combined
					</div>
				</div>

				{/* per game */}
				<SectionHead title="Per Game" k="pg" />
				{open.pg ? (
					<StatTable
						cols={PG_COLS}
						rows={baseRows}
						career={careerRow}
						careerShown={careerHasStats}
						colW={44}
					/>
				) : null}

				{/* advanced */}
				<SectionHead title="Advanced" k="adv" />
				{open.adv ? (
					<StatTable
						cols={ADV_COLS}
						rows={baseRows}
						career={careerRow}
						careerShown={careerHasStats}
						colW={48}
						advanced
					/>
				) : null}

				{/* shot locations */}
				<SectionHead title="Shot Locations" k="shot" />
				{open.shot ? (
					<div
						className="gmp-card"
						style={{ padding: "6px 14px 12px", marginTop: 8 }}
					>
						<div
							style={{
								fontSize: 9.5,
								fontWeight: 800,
								letterSpacing: ".4px",
								color: "#7f8b93",
								padding: "9px 0 4px",
							}}
						>
							{currentSeason} · attempts per game by zone
						</div>
						{shotZones.map((z) => {
							const col =
								z.pct >= 50 ? "#18E0A8" : z.pct >= 38 ? "#E8B923" : "#E2504E";
							return (
								<div
									key={z.zone}
									style={{ padding: "9px 0", borderTop: "1px solid #232323" }}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "baseline",
										}}
									>
										<div style={{ fontSize: 12.5, fontWeight: 800 }}>
											{z.zone}
										</div>
										<div
											style={{
												fontSize: 11,
												fontWeight: 700,
												color: "#8fb3c9",
											}}
										>
											{helpers.roundStat(z.m, "fg")}/
											{helpers.roundStat(z.a, "fga")} ·{" "}
											<span style={{ color: col, fontWeight: 900 }}>
												{z.pct}%
											</span>
										</div>
									</div>
									<div
										style={{
											height: 6,
											borderRadius: 4,
											background: "#242424",
											overflow: "hidden",
											marginTop: 7,
										}}
									>
										<div
											style={{
												height: "100%",
												width: `${Math.round((z.a / shotMax) * 100)}%`,
												borderRadius: 4,
												background: col,
											}}
										/>
									</div>
								</div>
							);
						})}
						<div style={{ display: "flex", gap: 8, marginTop: 12 }}>
							{feats.map((f) => (
								<div
									key={f.label}
									style={{
										flex: 1,
										background: "#111",
										border: "1px solid #262626",
										borderRadius: 10,
										padding: "9px 6px",
										textAlign: "center",
									}}
								>
									<div
										style={{
											fontSize: 16,
											fontWeight: 900,
											letterSpacing: "-.3px",
											color: "#18E0A8",
										}}
									>
										{f.v}
									</div>
									<div
										style={{
											fontSize: 8,
											fontWeight: 800,
											letterSpacing: ".2px",
											color: "#7f8b93",
											marginTop: 3,
										}}
									>
										{f.label}
									</div>
								</div>
							))}
						</div>
					</div>
				) : null}

				{/* game highs */}
				<SectionHead
					title="Game Highs"
					k="hi"
					right={
						<span style={{ fontSize: 10, fontWeight: 800, color: "#7f8b93" }}>
							{" "}
							· {currentSeason}
						</span>
					}
				/>
				{open.hi ? (
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(4,1fr)",
							gap: 8,
							marginTop: 8,
						}}
					>
						{HIGH_COLS.map(([label, key]) => {
							const raw = curStat[key];
							const v = Array.isArray(raw) ? raw[0] : (raw ?? 0);
							return (
								<div
									key={label}
									style={{
										background: "#181818",
										border: "1px solid #232323",
										borderRadius: 11,
										padding: "11px 4px",
										textAlign: "center",
									}}
								>
									<div
										style={{
											fontSize: 18,
											fontWeight: 900,
											letterSpacing: "-.3px",
										}}
									>
										{helpers.roundStat(v, key.replace("Max", ""), true)}
									</div>
									<div
										style={{
											fontSize: 8.5,
											fontWeight: 800,
											letterSpacing: ".3px",
											color: "#7f8b93",
											marginTop: 3,
										}}
									>
										{label}
									</div>
								</div>
							);
						})}
					</div>
				) : null}

				{/* salaries */}
				<SectionHead title="Salaries" k="sal" />
				{open.sal ? (
					<div
						className="gmp-card"
						style={{ padding: "4px 14px 6px", marginTop: 8 }}
					>
						{salaries.length === 0 ? (
							<div
								style={{
									padding: "14px 0",
									fontSize: 12,
									fontWeight: 700,
									color: "#7a7a7a",
									textAlign: "center",
								}}
							>
								No salary history.
							</div>
						) : (
							salaries.map((s) => (
								<div
									key={s.year}
									style={{ padding: "9px 0", borderTop: "1px solid #232323" }}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<div
											style={{
												fontSize: 12,
												fontWeight: 800,
												color: s.future ? "#8fb3c9" : "#4A9EFF",
											}}
										>
											{s.year}
										</div>
										<div
											style={{
												fontSize: 12.5,
												fontWeight: 900,
												color: s.future ? "#9fb0bc" : "#fff",
											}}
										>
											{money(s.amt)}
										</div>
									</div>
									<div
										style={{
											height: 5,
											borderRadius: 3,
											background: "#242424",
											overflow: "hidden",
											marginTop: 6,
										}}
									>
										<div
											style={{
												height: "100%",
												width: `${Math.round((s.amt / salMax) * 100)}%`,
												borderRadius: 3,
												background: s.future ? "#3a5f52" : "#18E0A8",
											}}
										/>
									</div>
								</div>
							))
						)}
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "12px 0 8px",
								borderTop: "1.5px solid #2f2f2f",
								marginTop: 2,
							}}
						>
							<div style={{ fontSize: 12.5, fontWeight: 900 }}>Total value</div>
							<div style={{ fontSize: 15, fontWeight: 900, color: "#18E0A8" }}>
								{money(salTotal)}
							</div>
						</div>
					</div>
				) : null}

				{/* scout note */}
				<div className="gmp-h">Scout note</div>
				<textarea
					value={note}
					onChange={(e) => saveNote(e.target.value)}
					placeholder="Add a note about this player…"
					className="gmp-note"
				/>
			</div>

			{toast ? <div className="gmp-toast">{toast}</div> : null}
		</div>
	);
};

const styles = `
.gmp-topbar { position:sticky; top:0; z-index:30; background:linear-gradient(180deg,#182a3a,#121212);
  padding:12px 12px 12px; display:flex; align-items:center; gap:6px; }
.gmp-round { width:34px; height:34px; flex:none; border-radius:50%; background:rgba(255,255,255,.08);
  display:flex; align-items:center; justify-content:center; font-size:20px; font-weight:900; cursor:pointer; }
.gmp-body { max-width:520px; margin:0 auto; padding:6px 16px 48px; }
.gmp-chip { display:inline-flex; align-items:center; gap:7px; background:#1c1c1c; border:1px solid #2a2a2a;
  border-radius:16px; padding:7px 13px; font-size:11px; font-weight:800; color:#cdd9e2; margin:6px 0 12px; }
.gmp-dot { width:7px; height:7px; border-radius:50%; background:#18E0A8; }
.gmp-hero { background:linear-gradient(150deg,#1c1c1c,#161616); border:1px solid #2a2a2a; border-radius:16px; padding:15px 15px 14px; }
.gmp-av { position:relative; width:60px; height:60px; flex:none; border-radius:16px; display:flex;
  align-items:center; justify-content:center; font-size:20px; font-weight:900; color:#0b0f13; }
.gmp-av-badge { position:absolute; bottom:-5px; right:-5px; width:24px; height:24px; border-radius:50%;
  background:#121212; border:2px solid #18E0A8; display:flex; align-items:center; justify-content:center;
  font-size:10px; font-weight:900; color:#18E0A8; }
.gmp-skill { min-width:22px; height:22px; padding:0 5px; border-radius:6px; background:#20342b; border:1px solid #2c5646;
  display:flex; align-items:center; justify-content:center; font-size:9px; font-weight:900; color:#18E0A8; }
.gmp-tag { display:flex; align-items:center; gap:5px; padding:0 9px; height:22px; border-radius:6px;
  background:#1b1b1b; border:1px solid #262626; font-size:9.5px; font-weight:900; color:#18E0A8; }
.gmp-tiles { display:grid; grid-template-columns:repeat(4,1fr); gap:7px; margin-top:14px; }
.gmp-btn-trade { flex:1; text-align:center; padding:12px; border-radius:23px; font-size:14px; font-weight:900;
  background:#18E0A8; color:#053a2c; cursor:pointer; }
.gmp-btn-cut { flex:none; padding:12px 30px; border-radius:23px; font-size:14px; font-weight:900; color:#ff6b66;
  border:1.5px solid #7a2f2d; background:rgba(226,80,78,.08); cursor:pointer; }
.gmp-actions { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-top:11px; }
.gmp-action { background:#181818; border:1px solid #232323; border-radius:13px; padding:12px 4px; text-align:center; cursor:pointer; }
.gmp-op { background:#181818; border:1px solid #232323; border-radius:13px; padding:13px 14px; }
.gmp-op-l { font-size:9.5px; font-weight:800; letter-spacing:.5px; color:#7f8b93; }
.gmp-h { margin-top:18px; font-size:15px; font-weight:900; letter-spacing:-.2px; }
.gmp-card { background:#181818; border:1px solid #232323; border-radius:13px; padding:12px 14px 13px; }
.gmp-grp-name { font-size:10.5px; font-weight:900; letter-spacing:.6px; color:#8fb3c9; padding-bottom:9px; border-bottom:1px solid #262626; }
.gmp-rt-label { width:74px; flex:none; font-size:11.5px; font-weight:700; color:#cdd4da; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.gmp-rt-track { flex:1; height:6px; border-radius:4px; background:#242424; overflow:hidden; }
.gmp-scroll::-webkit-scrollbar { width:0; height:0; }
.gmp-scroll { -ms-overflow-style:none; scrollbar-width:none; }
.gmp-note { width:100%; margin-top:10px; min-height:74px; resize:none; background:#181818; border:1px solid #2a2a2a;
  border-radius:12px; padding:12px 13px; color:#fff; font-size:12.5px; font-weight:600; line-height:1.5; outline:none;
  font-family:Montserrat,system-ui,sans-serif; box-sizing:border-box; }
.gmp-toast { position:fixed; left:50%; bottom:34px; z-index:60; transform:translateX(-50%); background:#fff; color:#000;
  font-size:12.5px; font-weight:800; padding:11px 18px; border-radius:24px; box-shadow:0 10px 30px rgba(0,0,0,.5); white-space:nowrap; }
`;

export default GmPlayer;
